import * as React from 'react';
import { Ellipsis } from '@common/components/Ellipsis';
import { Flex } from '@common/components/Flex';
import { Button } from '@common/components/Button';
import { Switch } from '@common/components/switch';
import { MessageConfirmParams } from '@common/hooks/message';
import { I18n } from 'react-redux-i18n';
import { InfoMessage } from '@common/components/info-message';

import './field.styles.scss';

export type FieldItem = {
  id: number | string;
  name: string;
  isVisible?: boolean;
};

export type FieldProps<Item extends FieldItem> = {
  data: Item;
  features: {
    delete: boolean;
    edit: boolean;
    changeShowStatus: boolean;
  };
  showDeleteConfirmMessage?: (props: MessageConfirmParams) => void;
  onEdit: (item: Item) => void;
  onDelete?: (item: Item) => void;
  onShowStatusChange?: (item: Item, status: boolean) => void;
};

export function Field<T extends FieldItem>(props: FieldProps<T>) {
  const { data, features, onEdit, onShowStatusChange, showDeleteConfirmMessage, onDelete } = props;
  const hasAnyFeature = Object.values(features).some((x) => x);

  const handleShowStatusChange = React.useCallback((isVisible: boolean) => {
    if (!onShowStatusChange) {
      return;
    }
    
    onShowStatusChange(data, isVisible);
  }, [onShowStatusChange, data]);

  const handleEditClick = React.useCallback(() => {
    onEdit(data);
  }, [onEdit, data]);

  const handleDeleteClick = React.useCallback(() => {
    if (!onDelete || !showDeleteConfirmMessage) {
      return;
    }

    showDeleteConfirmMessage({
      header: I18n.t('Confirmation Message'),
      content: <InfoMessage>{I18n.t('Are you sure you want to delete this field?')}</InfoMessage>,
      confirmText: I18n.t('Yes'),
      cancelText: I18n.t('No'),
      onCustomOk: () => {
        onDelete(data);
      }
    });
  }, [onDelete, showDeleteConfirmMessage, data]);

  return (
    <>
      <Ellipsis text={data.name} width="calc(100% - 60px)" />
      {hasAnyFeature && (
        <Flex alignItems="center">
          {features.edit && (
            <Button 
              className="FhqDesignToolFieldsField__action"  
              variant="text" 
              onClick={handleEditClick}
            >
              <i className="fa fa-pencil" />
            </Button>
          )}
          {features.delete && (
            <Button 
              className="FhqDesignToolFieldsField__action"
              variant="text"
              onClick={handleDeleteClick}
            >
              <i className="fa fa-trash" />
            </Button>
          )}
          {features.changeShowStatus && (
            <Switch
              className="FhqDesignToolFieldsField__action"
              checked={data.isVisible}
              onChange={handleShowStatusChange}
            />
          )}
        </Flex>
      )}
    </>
  );
};